import { CSSProperties, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import {
  WeekCalendarColumnProps,
  WeekCalendarDayProps,
  WeekCalendarHeadProps,
  WeekCalendarProps,
  WeekCalendarRowProps,
} from './types';

type DayVariant = 'today' | 'default';

const dayClasses: Record<DayVariant, string> = {
  today: 'bg-primary-content text-primary',
  default: 'bg-base-100 text-base-content/60',
};

const columnClasses: Record<DayVariant, string> = {
  today: 'bg-primary-content',
  default: 'bg-base-100',
};

const COLUMNS_VARIABLE = '--week-calendar-columns';

const GRID_CLASSNAME = 'grid grid-cols-[var(--week-calendar-columns)]';

export const WEEK_CALENDAR_HEAD_HEIGHT = 85;

const toColumnsTemplate = (columnCount: number) =>
  `repeat(${columnCount}, minmax(0, 1fr))`;

const WeekCalendarBase = forwardRef<HTMLDivElement, WeekCalendarProps>(
  ({ columnCount, classNames, children, style, ...props }, ref) => (
    <div
      className={twMerge(
        'overflow-x-auto rounded-2xl border border-base-300',
        classNames?.base,
      )}
    >
      <div
        ref={ref}
        aria-colcount={columnCount}
        className={twMerge('w-full min-w-[1200px]', classNames?.grid)}
        style={
          {
            [COLUMNS_VARIABLE]: toColumnsTemplate(columnCount),
            ...style,
          } as CSSProperties
        }
        {...props}
        role="grid"
      >
        {children}
      </div>
    </div>
  ),
);

const Head = forwardRef<HTMLDivElement, WeekCalendarHeadProps>(
  ({ className, children, style, ...props }, ref) => (
    <div
      ref={ref}
      className={twMerge(
        GRID_CLASSNAME,
        'sticky top-0 z-10 border-b border-base-300',
        className,
      )}
      style={{ height: WEEK_CALENDAR_HEAD_HEIGHT, ...style }}
      {...props}
      role="row"
    >
      {children}
    </div>
  ),
);

const Row = forwardRef<HTMLDivElement, WeekCalendarRowProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={twMerge(GRID_CLASSNAME, className)}
      {...props}
      role="row"
    >
      {children}
    </div>
  ),
);

const Day = forwardRef<HTMLDivElement, WeekCalendarDayProps>(
  (
    {
      abbreviation,
      dayNumber,
      today = false,
      todayLabel,
      classNames,
      ...props
    },
    ref,
  ) => (
    <div
      ref={ref}
      aria-current={today ? 'date' : undefined}
      className={twMerge(
        'p-2 font-semibold border-r border-base-300 last:border-r-0',
        dayClasses[today ? 'today' : 'default'],
        classNames?.base,
      )}
      {...props}
      role="columnheader"
    >
      <div
        className={twMerge(
          'flex h-full flex-col items-center justify-center gap-1',
          classNames?.content,
        )}
      >
        <span className="text-xs uppercase">{abbreviation}</span>
        <span className={twMerge('text-xl', !today && 'text-base-content')}>
          {dayNumber}
        </span>
        <ConditionalRenderer condition={today && !!todayLabel}>
          <span className="text-xs uppercase">{todayLabel}</span>
        </ConditionalRenderer>
      </div>
    </div>
  ),
);

const Column = forwardRef<HTMLDivElement, WeekCalendarColumnProps>(
  ({ today = false, classNames, children, ...props }, ref) => (
    <div
      ref={ref}
      className={twMerge(
        'p-2 border-r border-base-300 last:border-r-0',
        columnClasses[today ? 'today' : 'default'],
        classNames?.base,
      )}
      {...props}
      role="gridcell"
    >
      <div className={twMerge('flex h-full flex-col', classNames?.content)}>
        {children}
      </div>
    </div>
  ),
);

WeekCalendarBase.displayName = 'WeekCalendar';
Head.displayName = 'WeekCalendar.Head';
Row.displayName = 'WeekCalendar.Row';
Day.displayName = 'WeekCalendar.Day';
Column.displayName = 'WeekCalendar.Column';

export const WeekCalendar = Object.assign(WeekCalendarBase, {
  Head,
  Row,
  Day,
  Column,
});
